<template>
  <div>
    <v-card>
      <v-card-title>
        <span class="table-card-title">{{ tableTitle }}</span>
        <v-spacer />
        <v-col cols="7">
          <v-row class="align-center justify-end">
            <export-email-report-dialog
              v-if="voucherType === 'consumed'"
              button-text="Exportar vales-troca consumidos"
              title="Enviar relatório de vales-troca consumidos"
              description="O relatório de vales-troca consumidos é enviado, através de um e-mail, para a caixa de entrada dos endereços de e-mail adicionados abaixo. Um arquivo CSV ou XLSX contendo todas as informações detalhadas dos vales-troca consumidos é enviado em anexo ao e-mail."
              :exportReportFn="exportReport"
              async
              @file-format="getFileFormat"
            />
            <export-data v-else :data-fetcher="getDataToExport" :filename="getExportFileName()" button-label="Exportar dados" ghost class="mx-2" />
            <filters-menu v-if="filters.length" :filters="filters" default-date="ultimosTrintaDias" hide-ecommerce-configuration />
            <div class="search-container">
              <mf-search
                v-model="search"
                hide-details
                placeholder="Buscar por CPF/CNPJ ou nome"
                :disabled="!hasPiiAccess"
                :tooltipMessage="!hasPiiAccess ? 'Você precisa ter a permissão de acesso a informações privilegiadas para acessar esse recurso' : ''"
              />
            </div>
          </v-row>
        </v-col>
      </v-card-title>
      <v-card-text class="pa-0">
        <paginated-data-table
          ref="table"
          :headers="headers"
          :queryMainPropertyName="'items'"
          arrayFunction="filter"
          :queryVariables="queryVariables"
          :skip="skip"
          :search="search"
          :query="query"
          :maxRecords.sync="records"
          :queryName="queryName"
          :transformFunction="transformFunction"
          waitToSearch
          :loading="false"
          :useCustomHeadersSettings="false"
        >
          <template v-slot:item.consumer_name="{ item }">
            <span class="texto-corrido">{{ item.consumer_name }}</span>
          </template>
          <template v-slot:item.consumer_document_no="{ item }">
            <span :class="{ link: hasPiiAccess }" class="texto-corrido">
              <a v-if="hasPiiAccess" @click="openClientDetails(item.consumer_document_no)">
                {{ item.consumer_document_no | cpfCnpj }}
              </a>
              <v-tooltip v-else max-width="250" top content-class="truncated-tooltip">
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    {{ item.consumer_document_no }}
                  </span>
                </template>
                <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
              </v-tooltip>
            </span>
          </template>
          <template v-slot:item.unit.name="{ item }">
            <span class="texto-corrido">{{ item.unit.name }}</span>
          </template>
          <template v-slot:item.points="{ item }">
            <span class="texto-corrido">{{ item.points }}</span>
          </template>
          <template v-slot:item.money="{ item }">
            <span class="texto-corrido">R${{ item.money | localeString(2) }}</span>
          </template>
          <template v-slot:item.product_description="{ item }">
            <span class="texto-corrido">{{ formatDiscountProduct(item) }}</span>
          </template>
          <template v-slot:item.date="{ item }">
            <span class="texto-corrido">{{ setDate(item) }}</span>
          </template>
        </paginated-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import FiltersMenu from '@/components/FiltersMenu.vue'
import PaginatedDataTable from '@/components/PaginatedDataTable.vue'
import ExportData from '@/components/export-data/ExportData.vue'
import ExportEmailReportDialog from '@/components/export-email-report/ExportEmailReportDialog.vue'

import { DateTime } from 'luxon'
import { mapState } from 'vuex'
import { MUTATION_EXPORT_CONSUMED_VOUCHERS_REPORT } from '../../../graphql'

export default {
  name: 'VoucherTabItem',
  components: {
    PaginatedDataTable,
    FiltersMenu,
    ExportData,
    ExportEmailReportDialog,
  },
  props: {
    tableTitle: {
      type: String,
      required: true,
    },
    queryName: {
      type: String,
      required: true,
    },
    voucherType: {
      type: String,
      required: true,
    },
    dateName: {
      type: String,
      required: true,
    },
    query: {
      type: Object,
      required: true,
    },
    transformFunction: {
      type: Function,
      required: true,
    },
    tableHeaders: {
      type: Array,
      required: true,
    },
    filters: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    search: '',
    records: 0,
    DateTime: DateTime,
    headers: [],
    fileFormat: 'CSV',
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),
    hasInitialDate() {
      return this.stateFilters && this.stateFilters.data?.[0]
    },
    queryVariables() {
      let filters = {
        pointsVoucherHistoryType: this.voucherType.toUpperCase(),
        units: this.stateFilters.units || [],
      }
      if (this.voucherType === 'consumed' && this.stateFilters?.data) {
        filters.initialDate = this.stateFilters && this.stateFilters.data?.[0] ? `${this.stateFilters.data[0]}T00:00:00-03:00` : ''
        filters.finalDate = this.stateFilters && this.stateFilters.data?.[1] ? `${this.stateFilters.data[1]}T23:59:59-03:00` : ''
      }

      return filters
    },
    skip() {
      if (!this.queryVariables?.units?.length) return true
      if (this.voucherType === 'consumed') return !this.queryVariables?.initialDate || !this.queryVariables?.finalDate
      return false
    },
    dateSlotName() {
      return this.voucherType === 'consumed' ? 'item.consumed_at' : 'item.activated_at'
    },
    hasPiiAccess() {
      return this.$hasPiiAccess()
    },
  },
  mounted() {
    this.headers = this.getDefaultHeaders()
  },
  methods: {
    getFileFormat(value) {
      this.fileFormat = value
    },
    async exportReport(emails) {
      return this.$apollo.mutate({
        mutation: MUTATION_EXPORT_CONSUMED_VOUCHERS_REPORT,
        fetchPolicy: 'no-cache',
        variables: {
          emails,
          fileFormat: this.fileFormat,
          initial_date: this.queryVariables.initialDate || null,
          final_date: this.queryVariables.finalDate || null,
          units: this.queryVariables.units || [],
        },
        context: {
          uri: this.$microservicesUrls['asyncJobs'],
        },
      })
    },
    openClientDetails(document) {
      window.open(`#/clientes/detalhes-cliente/${document}`, '_blank')
    },
    formatDiscountProduct(item) {
      if (item.money) {
        return `R$ ${item.money.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      } else {
        return item.product_description || '-'
      }
    },
    formatDocument(document) {
      if (!document) return '-'

      const numbers = document.replace(/\D/g, '')

      if (numbers.length === 11) {
        return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
      }

      if (numbers.length === 14) {
        return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
      }

      return document
    },
    setDate(item) {
      const dateValue = item[this.dateName] || item.date
      if (!dateValue) return '-'
      return DateTime.fromISO(dateValue).toFormat('dd/MM/yyyy | HH:mm')
    },
    getDefaultHeaders() {
      return JSON.parse(JSON.stringify(this.tableHeaders))
    },
    getExportFileName() {
      const typeNames = {
        consumed: 'vales_troca_consumidos',
        activated: 'vales_troca_ativados',
        expired: 'vales_troca_expirados',
      }
      return typeNames[this.voucherType] || 'vales_troca_historico'
    },
    async getDataToExport() {
      try {
        const { data } = await this.$apollo.query({
          query: this.query,
          variables: {
            ...this.queryVariables,
            page: 1,
            pageSize: 1000,
            search: this.search,
            sortDirection: [],
            sort: [],
          },
          fetchPolicy: 'no-cache',
        })

        const items = data.pointsVoucherHistory?.items || []

        const headers = this.headers.filter((header) => header.isActive !== false).map((h) => h.text)

        const rows = items.map((item) => {
          const row = []
          this.headers.forEach((header) => {
            if (header.isActive !== false) {
              switch (header.value) {
                case 'consumer_document_no':
                  row.push(this.formatDocument(item.consumer_document_no))
                  break
                case 'consumer_name':
                  row.push(item.consumer_name || '-')
                  break
                case 'unit.name':
                  row.push(item.unit?.name || '-')
                  break
                case 'points':
                  row.push(item.points || '-')
                  break
                case 'money':
                  row.push(item.money ? `R$ ${item.money.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-')
                  break
                case 'product_description':
                  row.push(this.formatDiscountProduct(item))
                  break
                case 'date':
                  const dateValue = item[this.dateName] || item.date
                  row.push(dateValue ? DateTime.fromISO(dateValue).toFormat('dd/MM/yyyy | HH:mm') : '-')
                  break
                default:
                  row.push(item[header.value] || '-')
              }
            }
          })
          return row
        })

        return { headers, rows }
      } catch (error) {
        console.error('Erro ao buscar dados para exportação:', error)
        throw error
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.search-container {
  width: 300px;
  margin-left: 12px;
}

.text-lower {
  text-transform: none;
}
.search-input {
  min-width: 300px;
  padding-bottom: 15px;
}
</style>
